import type React from "react"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Link } from "react-router-dom"
import { cn } from "../../lib/utils"
import { useNotifications } from "../hooks/use-notifications"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useUser } from "../user-context"
import { notificationCopy } from "./notification-copy"
import {
	RAIL_CONTROL_CLASS,
	RAIL_CONTROL_ICON_CLASS,
	RAIL_ROW_EXPANDED_CLASS,
	RailTooltip,
} from "./rail-controls"

function BellIcon({ size = 18 }: { size?: number }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
			<path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
		</svg>
	)
}

/**
 * Persisted-notification bell (ZPL-310): lives INSIDE the chrome (sidebar
 * footer on desktop, top header on mobile) — never a floating overlay, so
 * it cannot collide with page-level action bars. Unread badge fed live by
 * `notification.created` on the per-user channel; the dropdown lists the
 * feed with actor-led one-liners and per-type deep links. Opening it marks
 * everything read.
 */
export const NotificationBell: React.FC<{
	placement: "sidebar" | "mobile-header"
	expanded?: boolean
}> = ({ placement, expanded = false }) => {
	const { profile } = useUser()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { feed, markAllRead } = useNotifications(profile?.id)
	const [open, setOpen] = useState(false)
	const rootRef = useRef<HTMLDivElement>(null)
	// The drawer portals to <body> (outside the rail's stacking context), so
	// outside-click containment must check it separately from the trigger.
	const drawerRef = useRef<HTMLElement>(null)

	useEffect(() => {
		if (!open) return
		const onDown = (e: MouseEvent) => {
			const target = e.target as Node
			if (
				!rootRef.current?.contains(target) &&
				!drawerRef.current?.contains(target)
			) {
				setOpen(false)
			}
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false)
		}
		window.addEventListener("mousedown", onDown)
		window.addEventListener("keydown", onKey)
		return () => {
			window.removeEventListener("mousedown", onDown)
			window.removeEventListener("keydown", onKey)
		}
	}, [open])

	// Drawer enter animation: mounts translated behind the rail, then slides
	// right on the next frame (the rail sits at z-40, the drawer below it).
	const [entered, setEntered] = useState(false)
	useEffect(() => {
		if (!open) {
			setEntered(false)
			return
		}
		const id = requestAnimationFrame(() => setEntered(true))
		return () => cancelAnimationFrame(id)
	}, [open])

	if (!profile) return null
	const unread = feed.data?.unreadCount ?? 0
	const items = feed.data?.notifications ?? []
	const sorted = [...items].sort((a, b) =>
		b.createdAt.localeCompare(a.createdAt),
	)

	const toggle = () => {
		const next = !open
		setOpen(next)
		if (next && unread > 0) markAllRead.mutate()
	}

	const badge =
		unread > 0 ? (
			<span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] leading-4 text-center font-semibold">
				{unread > 99 ? "99+" : unread}
			</span>
		) : null

	return (
		<div
			ref={rootRef}
			className={placement === "sidebar" ? "w-full" : "relative"}
		>
			{placement === "sidebar" ? (
				expanded ? (
					<button
						type="button"
						onClick={toggle}
						aria-label={t.notificationsBell.title}
						aria-expanded={open}
						className={RAIL_ROW_EXPANDED_CLASS}
					>
						<span
							aria-hidden="true"
							className={cn("relative", RAIL_CONTROL_ICON_CLASS)}
						>
							<BellIcon size={18} />
							{badge}
						</span>
						<span className="truncate text-sm text-muted-foreground">
							{t.notificationsBell.title}
						</span>
					</button>
				) : (
					<button
						type="button"
						onClick={toggle}
						aria-label={t.notificationsBell.title}
						aria-expanded={open}
						className={RAIL_CONTROL_CLASS}
					>
						<span
							aria-hidden="true"
							className={cn("relative", RAIL_CONTROL_ICON_CLASS)}
						>
							<BellIcon size={18} />
							{badge}
						</span>
						<RailTooltip label={t.notificationsBell.title} />
					</button>
				)
			) : (
				<button
					type="button"
					onClick={toggle}
					aria-label={t.notificationsBell.title}
					aria-expanded={open}
					className="relative flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring h-6 w-6"
				>
					<BellIcon size={15} />
					{badge}
				</button>
			)}
			{open &&
				placement === "sidebar" &&
				createPortal(
					<>
						{/* Gray page overlay — the rail (z-40) stays above; clicking
					    anywhere outside closes (overlay here, rail/page clicks via
					    the outside-mousedown handler). */}
						<div
							data-testid="notifications-overlay"
							aria-hidden="true"
							onClick={() => setOpen(false)}
							className="fixed inset-0 z-30 bg-foreground/30 dark:bg-black/60"
						/>
						<aside
							ref={drawerRef}
							role="dialog"
							aria-label={t.notificationsBell.title}
							className={cn(
								"fixed top-0 bottom-0 z-[32] flex w-[380px] flex-col border-r border-border bg-card shadow-2xl",
								expanded ? "left-rail-expanded" : "left-rail",
								expanded
									? "max-w-[calc(100vw-var(--spacing-rail-expanded))]"
									: "max-w-[calc(100vw-var(--spacing-rail))]",
								"transform-gpu transition-transform duration-200 motion-reduce:transition-none",
								entered ? "translate-x-0" : "-translate-x-full",
							)}
						>
							<header className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
								<h2 className="text-sm font-semibold text-foreground">
									{t.notificationsBell.title}
								</h2>
								<button
									type="button"
									onClick={() => setOpen(false)}
									aria-label={t.notificationsBell.close}
									className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground/80 transition-colors hover:bg-accent hover:text-foreground"
								>
									<svg
										width={16}
										height={16}
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										aria-hidden="true"
									>
										<path d="M18 6 6 18M6 6l12 12" />
									</svg>
								</button>
							</header>
							<div className="flex-1 overflow-y-auto">
								{sorted.length === 0 ? (
									<div className="px-4 py-8 text-sm text-muted-foreground text-center">
										{t.notificationsBell.empty}
									</div>
								) : (
									<ul>
										{sorted.map((n) => {
											const { text, href } = notificationCopy(t, n)
											return (
												<li
													key={n.id}
													className="border-b border-border last:border-b-0"
												>
													<Link
														to={href}
														onClick={() => setOpen(false)}
														className={`block px-4 py-3 text-sm hover:bg-accent transition-colors ${
															n.readAt
																? "text-muted-foreground"
																: "text-foreground"
														}`}
													>
														{text}
													</Link>
												</li>
											)
										})}
									</ul>
								)}
							</div>
						</aside>
					</>,
					document.body,
				)}
			{open && placement === "mobile-header" && (
				<div className="absolute w-80 max-h-96 overflow-y-auto rounded-xl border border-border bg-popover text-popover-foreground shadow-xl z-50 right-0 top-full mt-2">
					<div className="px-3 py-2 text-sm font-semibold border-b border-border">
						{t.notificationsBell.title}
					</div>
					{sorted.length === 0 ? (
						<div className="px-3 py-6 text-sm text-muted-foreground text-center">
							{t.notificationsBell.empty}
						</div>
					) : (
						<ul>
							{sorted.map((n) => {
								const { text, href } = notificationCopy(t, n)
								return (
									<li
										key={n.id}
										className="border-b border-border last:border-b-0"
									>
										<Link
											to={href}
											onClick={() => setOpen(false)}
											className={`block px-3 py-2 text-sm hover:bg-accent transition-colors ${
												n.readAt ? "text-muted-foreground" : "text-foreground"
											}`}
										>
											{text}
										</Link>
									</li>
								)
							})}
						</ul>
					)}
				</div>
			)}
		</div>
	)
}
